import { useCallback, useState, useEffect } from "react";
import Dropzone from "react-dropzone";
import { Button } from "../../components/ui/button";
import { FileText, Upload, X, Check, ChevronsUpDown } from "lucide-react";
import { fetchFastApi } from "../../api/fastapi";
import { toast } from "sonner";
import { useReportExtractionSchemas } from "../../hooks/useReportExtractionSchemas";
import { useLocation, useNavigate } from "react-router-dom";
import { validateFileSizeWithToast } from "../../lib/fileUploadValidation";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandGroup,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { cn } from "../../lib/utils";

export const NewReportExtractionPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [schemaId, setSchemaId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [schemaPopoverOpen, setSchemaPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Mounted under both /audit and /dashboard; return to the jobs list in the
  // tree the user came from. See ReportExtractionsPage for why.
  const reportExtractionBase = pathname.startsWith("/audit/")
    ? "/audit/report-extraction"
    : "/dashboard/report-extraction";

  const { schemas } = useReportExtractionSchemas({ fetchOnLoad: true });

  // Auto-select first schema when loaded
  useEffect(() => {
    if (!schemaId && schemas.length > 0) {
      setSchemaId(schemas[0].schemaId);
    }
  }, [schemas, schemaId]);

  const onFileDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const droppedFile = acceptedFiles[0];
      if (!validateFileSizeWithToast(droppedFile)) return;
      setFile(droppedFile);
    }
  };

  const submitExtraction = useCallback(async () => {
    if (!file) {
      toast.error("Please upload a PDF file");
      return;
    }

    if (!schemaId) {
      toast.error("Please select a schema");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("schema_id", schemaId);

      await fetchFastApi({
        method: "POST",
        path: "/report-extractions",
        body: formData,
      });

      toast.success("Report extraction started");
      navigate(`${reportExtractionBase}/jobs`);
    } catch (error) {
      console.error("Error submitting extraction:", error);
      toast.error("Error starting extraction");
    } finally {
      setLoading(false);
    }
  }, [file, schemaId, navigate, reportExtractionBase]);

  const deleteFile = () => {
    setFile(null);
  };

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center w-full bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
      <div className="max-w-2xl w-full py-12 px-20">
        <div className="flex flex-col">
          <div className="text-blue-700">Midship</div>
          <div className="text-3xl mt-2">Extract Report Data</div>

          <div className="flex gap-4 mt-6">
            <div className="flex flex-col w-1/2 gap-4 p-4 rounded-lg bg-stone-100">
              <div className="text-lg">Source File</div>
              {file && <UploadedFile file={file} deleteFile={deleteFile} />}

              {!file && (
                <Dropzone
                  onDrop={onFileDrop}
                  accept={acceptedFileTypes}
                  maxFiles={1}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div
                        {...getRootProps()}
                        className="flex rounded-md gap-2 items-center p-2 bg-white border border-stone-200 cursor-pointer"
                        role="button"
                        tabIndex={0}
                      >
                        <input {...getInputProps()} accept=".pdf" />
                        <Upload size={20} />
                        <p className="text-sm">Upload PDF</p>
                      </div>
                    </section>
                  )}
                </Dropzone>
              )}
            </div>

            <div className="flex flex-col w-1/2 gap-4 p-4 rounded-lg bg-stone-100">
              <div className="text-lg">Schema</div>
              <Popover
                open={schemaPopoverOpen}
                onOpenChange={setSchemaPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={schemaPopoverOpen}
                    className="w-full justify-between hover:bg-white"
                  >
                    <span className="truncate font-normal">
                      {schemaId
                        ? schemas?.find((s) => s.schemaId === schemaId)?.name
                        : "Select schema..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command
                    className="max-h-[300px] w-[250px] overflow-y-auto"
                    filter={(id, query) => {
                      const schema = schemas?.find((s) => s.schemaId === id);
                      if (
                        schema?.name.toLowerCase().includes(query.toLowerCase())
                      ) {
                        return 1;
                      }
                      return 0;
                    }}
                  >
                    <CommandInput placeholder="Search schema..." />
                    <CommandEmpty>No schema found.</CommandEmpty>
                    <CommandList>
                      <CommandGroup heading="Available Schemas">
                        {(schemas || []).map((schema) => (
                          <CommandItem
                            key={schema.schemaId}
                            value={schema.schemaId}
                            onSelect={() => {
                              setSchemaId(schema.schemaId);
                              setSchemaPopoverOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                schema.schemaId === schemaId
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <span>{schema.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex">
            <Button
              className="mt-6 w-40"
              onClick={submitExtraction}
              loading={loading}
              disabled={!file || !schemaId}
            >
              Extract
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadedFile = ({
  file,
  deleteFile,
}: {
  file: File;
  deleteFile: () => void;
}) => (
  <div className="flex items-center justify-between gap-2 p-2 bg-white rounded-md group border border-stone-200">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <FileText size={20} className="shrink-0" />
      <div className="truncate">{file.name}</div>
    </div>

    <X
      size={20}
      className="shrink-0 cursor-pointer invisible group-hover:visible"
      onClick={deleteFile}
    />
  </div>
);
